import { Typography, EllipsisMenu, EditIcon, Bin, IOption } from '@weareopenr/component-library';
import { IExtensionInstance } from 'src/api/extensions';
import { usePermissions } from 'src/hooks';
import styles from './Extensions.module.scss';

interface IInstanceRowProps {
  instance: IExtensionInstance;
  onEdit: () => void;
  onDelete: () => void;
}

const getInstanceName = (name: string): string => {
  return name || 'Unnamed Instance';
};

const getSettingsPart = (instance: IExtensionInstance): string[] => {
  const settingsCount = Object.keys(instance.settings).length;
  if (settingsCount === 0) return ['No settings'];

  const parts: string[] = [];
  const { external_id: externalId } = instance.settings;
  if (externalId) parts.push(`external_id: ${externalId}`);

  const otherSettings = settingsCount - (externalId ? 1 : 0);
  if (otherSettings > 0) {
    parts.push(`${otherSettings} setting${otherSettings > 1 ? 's' : ''}`);
  }
  return parts;
};

const getEndpointsPart = (instance: IExtensionInstance): string | null => {
  const { endpoints } = instance;
  if (endpoints.length === 0) return null;

  const enabledCount = endpoints.filter((e) => e.is_enabled).length;
  if (enabledCount === endpoints.length) {
    return `${endpoints.length} endpoint${endpoints.length > 1 ? 's' : ''}`;
  }
  return `${enabledCount}/${endpoints.length} endpoints enabled`;
};

const getSettingsSummary = (instance: IExtensionInstance): string => {
  const parts = getSettingsPart(instance);
  const endpointsPart = getEndpointsPart(instance);
  if (endpointsPart) parts.push(endpointsPart);
  return parts.join(' \u2022 ');
};

export const InstanceRow = ({ instance, onEdit, onDelete }: IInstanceRowProps) => {
  const { canEditExtensions, canUninstallExtensions } = usePermissions();

  const menuOptions: IOption[] = [];
  if (canEditExtensions) {
    menuOptions.push({
      id: 'edit',
      name: 'Edit',
      onOptionClick: onEdit,
      icon: EditIcon,
    });
  }
  if (canUninstallExtensions) {
    menuOptions.push({
      id: 'delete',
      name: 'Delete',
      onOptionClick: onDelete,
      icon: Bin,
      hoverColour: 'tomato',
    });
  }

  return (
    <div className={styles['instance-row']}>
      <div className={styles['instance-info']}>
        <Typography weight="semi-bold" className={styles['instance-name']}>
          {getInstanceName(instance.name)}
        </Typography>
        <Typography className={styles['instance-meta']}>{getSettingsSummary(instance)}</Typography>
      </div>
      {menuOptions.length > 0 && <EllipsisMenu options={menuOptions} variant="outline" />}
    </div>
  );
};
